import React, { useEffect, useRef, useState } from "react";
import { Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { api } from "../../lib/api";
import { getSocket } from "../../lib/socket";
import Attachments from "./Attachments";

function fmtTime(d) { return new Date(d).toLocaleString([], { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" }); }

export default function ThreadDrawer({ isOpen, onClose, parentId, channelId }) {
  const meId = localStorage.getItem("userID");
  const [parent, setParent] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  async function load() {
    if (!parentId) return;
    setLoading(true);
    try {
      const r = await api(`/chat/messages/${parentId}/replies`);
      setParent(r.parent);
      setReplies(r.replies || []);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "auto" }), 30);
    } catch (e) { /* swallow */ }
    finally { setLoading(false); }
  }

  useEffect(() => { if (isOpen && parentId) load(); /* eslint-disable-next-line */ }, [isOpen, parentId]);

  // Real-time: append new replies in this thread
  useEffect(() => {
    if (!isOpen) return;
    const socket = getSocket();
    if (!socket) return;
    function onNew({ message }) {
      if (!message || String(message.parent || "") !== String(parentId)) return;
      setReplies((prev) => prev.some((m) => String(m._id) === String(message._id)) ? prev : [...prev, message]);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
    }
    function onEdit({ message }) {
      if (!message) return;
      if (String(message._id) === String(parentId)) setParent(message);
      else setReplies((prev) => prev.map((m) => String(m._id) === String(message._id) ? message : m));
    }
    function onDelete({ messageId }) {
      setReplies((prev) => prev.filter((m) => String(m._id) !== String(messageId)));
    }
    socket.on("message:new", onNew);
    socket.on("message:edit", onEdit);
    socket.on("message:delete", onDelete);
    return () => { socket.off("message:new", onNew); socket.off("message:edit", onEdit); socket.off("message:delete", onDelete); };
  }, [isOpen, parentId]);

  async function send() {
    const t = text.trim();
    if (!t || !channelId || !parentId) return;
    setText("");
    try {
      await api(`/chat/channels/${channelId}/messages`, {
        method: "POST",
        body: JSON.stringify({ text: t, parent: parentId }),
      });
    } catch (e) { /* surfaced via toast in parent if needed */ }
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" pb={3}>
          <div className="text-sm font-semibold text-ink-800">Thread</div>
          <div className="text-[11px] text-ink-500">{replies.length} {replies.length === 1 ? "reply" : "replies"}</div>
        </DrawerHeader>
        <DrawerBody p={0}>
          <div className="flex flex-col" style={{ height: "calc(100vh - 140px)" }}>
            {loading || !parent ? (
              <div className="flex-1 flex items-center justify-center text-xs text-ink-400">Loading…</div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-ink-100 bg-ink-50/50">
                  <div className="flex items-start gap-2">
                    <RouterLink to={`/users/${parent.author?._id}`}>
                      <div className="w-7 h-7 rounded-full bg-ink-200 text-ink-600 text-[10px] font-semibold flex items-center justify-center hover:ring-2 hover:ring-blue-200">
                        {(parent.author?.username || "?").charAt(0).toUpperCase()}
                      </div>
                    </RouterLink>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-ink-700">{parent.author?.username || "—"}</span>
                        <span className="text-[10px] text-ink-400">{fmtTime(parent.createdAt)}</span>
                      </div>
                      {parent.text && <div className="text-sm text-ink-800 whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: parent.text }} />}
                      <Attachments files={parent.attachments} />
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {replies.length === 0 ? (
                    <div className="text-center text-xs text-ink-400 py-6">Start the thread.</div>
                  ) : replies.map((m, i) => {
                    const prev = replies[i - 1];
                    const sameAuthor = prev && String(prev.author?._id) === String(m.author?._id) && new Date(m.createdAt) - new Date(prev.createdAt) < 5 * 60 * 1000;
                    return (
                      <div key={m._id} className="flex gap-2 items-start">
                        <div className="w-7 flex-shrink-0">
                          {!sameAuthor && (
                            <div className="w-7 h-7 rounded-full bg-ink-200 text-ink-600 text-[10px] font-semibold flex items-center justify-center">
                              {(m.author?.username || "?").charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {!sameAuthor && (
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-semibold text-ink-700">{m.author?.username || "—"}</span>
                              <span className="text-[10px] text-ink-400">{fmtTime(m.createdAt)}</span>
                            </div>
                          )}
                          {m.text && <div className="text-sm text-ink-800 whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: m.text }} />}
                          <Attachments files={m.attachments} />
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>

                <div className="p-3 border-t border-ink-100 bg-surface">
                  <div className="flex items-center gap-2">
                    <input
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder="Reply…"
                      className="flex-1 text-sm border border-ink-200 rounded px-3 py-1.5 outline-none focus:border-blue-300"
                    />
                    <button onClick={send} disabled={!text.trim()}
                      className={`px-3 py-1.5 rounded text-sm font-semibold text-white ${text.trim() ? "bg-blue-500 hover:bg-blue-600" : "bg-ink-300 cursor-not-allowed"}`}>
                      Send
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
